import React from "react";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { t } from "@client/lib/dictionary";
import type { PipTelemetry } from "@client/types/pip";
import { formatMetric, metricLabel } from "@client/utils/formatMetric";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  slug: string;
  telemetry: PipTelemetry | undefined;
  compact?: boolean;
}

const Metric: React.FC<{ label: string; value: string; tone?: "good" | "warn" | "bad" }> = ({ label, value, tone }) => {
  const colorClass =
    tone === "good"
      ? "text-green-600"
      : tone === "warn"
        ? "text-amber-600"
        : tone === "bad"
          ? "text-red-600"
          : "text-foreground";
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xl font-bold tabular-nums ${colorClass}`}>{value}</span>
    </div>
  );
};

export const PipOverviewCard: React.FC<Props> = ({ slug, telemetry, compact = false }) => {
  const topic = (metric: string) => `zige/pip-${slug}/${metric}/val`;
  const gridVMeta = useMetricMeta(topic("grid-v"));
  const gridHzMeta = useMetricMeta(topic("grid-hz"));
  const acOutVMeta = useMetricMeta(topic("ac-out-v"));
  const acOutWMeta = useMetricMeta(topic("ac-out-w"));
  const loadMeta = useMetricMeta(topic("ac-out-percent"));
  const busVMeta = useMetricMeta(topic("bus-v"));
  const batteryVMeta = useMetricMeta(topic("battery-v"));
  const batterySocMeta = useMetricMeta(topic("battery-soc"));
  const batteryChargeAMeta = useMetricMeta(topic("battery-charge-a"));
  const batteryDischargeAMeta = useMetricMeta(topic("battery-discharge-a"));
  const pvVMeta = useMetricMeta(topic("pv-v"));
  const pvWMeta = useMetricMeta(topic("pv-w"));
  const heatsinkMeta = useMetricMeta(topic("heatsink-temp"));
  const pv2VMeta = useMetricMeta(topic("pv2-v"));
  const pv2WMeta = useMetricMeta(topic("pv2-w"));

  if (!telemetry) {
    return (
      <Card>
        <CardContent className="p-6 text-muted-foreground text-center">Čekám na první telemetrii…</CardContent>
      </Card>
    );
  }

  const socTone: "good" | "warn" | "bad" =
    telemetry.batterySocPercent < 20 ? "bad" : telemetry.batterySocPercent < 50 ? "warn" : "good";
  const tempTone: "good" | "warn" | "bad" =
    telemetry.heatsinkTempC > 75 ? "bad" : telemetry.heatsinkTempC > 60 ? "warn" : "good";

  return (
    <Card>
      {!compact && (
        <CardHeader>
          <CardTitle>{t.pip.detail.overview}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${compact ? "p-4" : ""}`}>
        <Metric
          label={metricLabel(gridVMeta, t.pip.metrics.gridV)}
          value={formatMetric(telemetry.gridVoltageV, gridVMeta)}
        />
        <Metric
          label={metricLabel(gridHzMeta, t.pip.metrics.gridHz)}
          value={formatMetric(telemetry.gridFrequencyHz, gridHzMeta)}
        />
        <Metric
          label={metricLabel(acOutVMeta, t.pip.metrics.acOutV)}
          value={formatMetric(telemetry.acOutputVoltageV, acOutVMeta)}
        />
        <Metric
          label={metricLabel(acOutWMeta, t.pip.metrics.acOutW)}
          value={formatMetric(telemetry.acOutputActiveW, acOutWMeta)}
        />
        <Metric
          label={metricLabel(loadMeta, t.pip.metrics.load)}
          value={formatMetric(telemetry.outputLoadPercent, loadMeta)}
        />
        <Metric
          label={metricLabel(busVMeta, t.pip.metrics.busV)}
          value={formatMetric(telemetry.busVoltageV, busVMeta)}
        />
        <Metric
          label={metricLabel(batteryVMeta, t.pip.metrics.batteryV)}
          value={formatMetric(telemetry.batteryVoltageV, batteryVMeta)}
        />
        <Metric
          label={metricLabel(batterySocMeta, t.pip.metrics.batterySoc)}
          value={formatMetric(telemetry.batterySocPercent, batterySocMeta)}
          tone={socTone}
        />
        <Metric
          label={metricLabel(batteryChargeAMeta, t.pip.metrics.batteryChargeA)}
          value={formatMetric(telemetry.batteryChargingA, batteryChargeAMeta)}
        />
        <Metric
          label={metricLabel(batteryDischargeAMeta, t.pip.metrics.batteryDischargeA)}
          value={formatMetric(telemetry.batteryDischargeA, batteryDischargeAMeta)}
        />
        <Metric
          label={metricLabel(pvVMeta, t.pip.metrics.pvV)}
          value={formatMetric(telemetry.pvInputVoltageV, pvVMeta)}
        />
        <Metric
          label={metricLabel(pvWMeta, t.pip.metrics.pvW)}
          value={formatMetric(telemetry.pvChargingPowerW, pvWMeta)}
        />
        <Metric
          label={metricLabel(heatsinkMeta, t.pip.metrics.heatsink)}
          value={formatMetric(telemetry.heatsinkTempC, heatsinkMeta)}
          tone={tempTone}
        />
        {telemetry.pv2InputVoltageV !== null && (
          <>
            <Metric
              label={metricLabel(pv2VMeta, t.pip.metrics.pv2V)}
              value={formatMetric(telemetry.pv2InputVoltageV, pv2VMeta)}
            />
            <Metric
              label={metricLabel(pv2WMeta, t.pip.metrics.pv2W)}
              value={formatMetric(telemetry.pv2ChargingPowerW ?? 0, pv2WMeta)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
